import { Bubble, Sender } from "@ant-design/x";
import type { BubbleItemType, BubbleListProps, SenderProps } from "@ant-design/x";
import { XbotAnswerToQuestion, XbotCapabilities, XbotHistory, XbotHistoryMessage, XbotPageContext, XbotStarters, XbotSuggestion } from "@dto/pyxbot-agent.dto";
import { DateUtil } from "@dto/util/DateUtil";
import { Avatar, Button, Tooltip } from "antd";
import { ComponentRef, FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LuBotMessageSquare, LuHistory, LuMessageSquarePlus } from "react-icons/lu";
import { useNavigate } from "react-router";
import { GLOB } from "src/util/Glob";
import { v4 as uuidv4 } from "uuid";
import { ChatContext } from "./AIChat";
import { upsertPyxbotHistory } from "./chatService";
import { signedInHistoryOwner } from "./historyOwner";
import { useAbortPrevious } from "src/hook/useAbortController";
import { AIChatStarters } from "./components/StarterPrompts";
import { AskingAboutBar } from "./components/AskingAboutBar";
import { SuggestRow } from "./components/SuggestRow";
import { readXbotCapabilities } from "./suggestions/client";
import {
  acceptSuggestion, applyEdit, caretAfterJump, caretOf, Draft, DraftHistory, EMPTY_DRAFT, finalizeDraft, insertSuggestionAt,
  isSmallEdit, pushHistory, redo, removeBinding, restoredDraft, SentDraft, sentDraftOf, startHistory, undo,
} from "./suggestions/draft";
import { ObjectSearch } from "./suggestions/ObjectSearch";
import { SelectedRow, SuggestButton, SuggestionList } from "./suggestions/SuggestionList";
import { useSuggestions } from "./suggestions/useSuggestions";
import { closeXbotThread, loadXbotPage, sendXbotChat } from "./agent/chatClient";
import {
  afterStop, answerText, AskedBefore, askedBefore, assistantEntry, beforeNewTurn, hasSomethingToSend, lastAnswerKey, newXbotConversation, OpenThreads,
  settleTurn, storedChatItems, threadAfterStarters, userEntry, withoutAnswerLine, xbotChatBody, XbotChatItem, XbotOutgoing, withTurn,
} from "./agent/chatThread";
import type { XbotTurn } from "./agent/turnState";
import { XbotAnswerTools, XbotFramedAnswer } from "./agent/XbotAnswerTools";
import { XbotProgressBar } from "./agent/XbotProgressBar";
import { Label } from "src/data/Label";

/** Said under the editor when an object found by name could not stand where the cursor was. */
const SEARCH_PICK_FAILED = "The object could not be put there; place the cursor between words and try again.";

/** Shown when a message carries picks the service can't accept yet. */
const ANSWER_HEARD = `${Label.XORBOT} answered.`;
const PICKS_NEED_UPDATE = `Picked objects cannot be sent: the ${Label.XORBOT} service needs updating. Remove the selection to send the text.`;

const BOT_AVATAR = <Avatar className="xm-chat-avatar" size={28} icon={<LuBotMessageSquare />} />;

const ANSWER_BUBBLE = {
  root: { width: "100%", maxWidth: "100%", minWidth: 0, paddingInlineEnd: 8, boxSizing: "border-box" as const },
  // `width: 100%` above means the whole row, so `body` uses flex to leave room for the avatar.
  body: { flex: "1 1 0", minWidth: 0, maxWidth: "100%" },
  content: { width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" as const },
  footer: { display: "block", width: "100%", maxWidth: "100%", minWidth: 0 },
};

/** An answer and a refusal in its place share one look. */
const ANSWER_ROLE = {
  placement: "start",
  // One frame for the whole answer, on the card ground its charts and tables are drawn on (agent/xbot.less).
  variant: "outlined",
  shape: "corner",
  avatar: BOT_AVATAR,
  styles: ANSWER_BUBBLE,
} as const;

const ROLES: BubbleListProps["role"] = {
  ai: {
    ...ANSWER_ROLE,
    classNames: { content: "xm-chat-answer-bubble", body: "xm-chat-answer-body" },
    contentRender: (content) => <div className="xm-chat-plain">{content as string}</div>,
  },
  user: {
    placement: "end",
    variant: "filled",
    shape: "corner",
  },
  error: {
    ...ANSWER_ROLE,
    classNames: { content: "xm-chat-error xm-chat-answer-bubble", body: "xm-chat-answer-body" },
    // the chat's own sentences ("XorMon did not answer"), never the model's: plain words, said to a screen reader at once
    contentRender: (content) => <div className="xm-chat-plain" role="alert">{content as string}</div>,
  },
};

/** One thread lives for the whole conversation, so reopening it from history continues where it left off. */
export const AIChatConversation: FC = () => {
  const { conversation, setConversation, setHistory, chatOpen, setHistoryOpen, pageContext, onPageOpened } = useContext(ChatContext);
  const navigate = useNavigate();
  const [items, setItems] = useState<XbotChatItem[]>([]);
  // One message out at a time: a new one, a new or another conversation and Stop cancel the one still out; so does leaving XorMon (signing out).
  const [nextChatSignal, abortChat] = useAbortPrevious();
  // Said to a screen reader when an answer has come (a refusal says itself, role="alert"); every second one ends in a
  // no-break space, as a reader repeats only words that changed.
  const [heard, setHeard] = useState("");
  const [context, setContext] = useState<XbotPageContext | null | undefined>(pageContext);
  /** Starters are kept together with the page they were fetched for. */
  const [starters, setStarters] = useState<{ page: XbotPageContext | null | undefined; starters: XbotStarters } | null>(null);
  const [busy, setBusy] = useState(false);
  /** Ref mirrors draft so handlers can read the latest value between renders. */
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const draftRef = useRef<Draft>(EMPTY_DRAFT);
  const historyRef = useRef<DraftHistory>(startHistory(EMPTY_DRAFT));
  /** Re-keyed for the suggestion service after every send or clear. */
  const [draftId, setDraftId] = useState(() => uuidv4());
  const caretAfterRef = useRef<number | null>(null);
  const [composerNote, setComposerNote] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<XbotCapabilities | null>(null);
  const [nextCapabilitiesSignal] = useAbortPrevious();
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const senderRef = useRef<ComponentRef<typeof Sender>>(null);
  const listRef = useRef<ComponentRef<typeof Bubble.List>>(null);
  const keySeq = useRef(0);
  const pendingKeyRef = useRef("");
  const openThreads = useRef(new OpenThreads());
  // Ant Design X's autoScroll only follows when already at the bottom; force it after sending so a long answer doesn't strand the view.
  const followLatestRef = useRef(false);
  const ignoreScrollRef = useRef(false);

  const nextKey = (prefix: string) => `${prefix}-${++keySeq.current}`;

  /** The editor shows `next`, and undo can go back to what was there before; a note about the old draft goes. */
  const showDraft = useCallback((next: Draft, small = false) => {
    setComposerNote(null);
    const before = draftRef.current;
    if (next === before) {
      return;
    }
    draftRef.current = next;
    setDraft(next);
    historyRef.current = pushHistory(historyRef.current, next, { now: Date.now(), small });
  }, []);

  const resetDraft = useCallback(() => {
    draftRef.current = EMPTY_DRAFT;
    setDraft(EMPTY_DRAFT);
    historyRef.current = startHistory(EMPTY_DRAFT);
    setDraftId(uuidv4());
    setComposerNote(null);
  }, []);

  /** Called on open, and again after the service reports it doesn't support something. */
  const refreshCapabilities = useCallback(() => {
    const signal = nextCapabilitiesSignal();
    void readXbotCapabilities(signal).then((taken) => {
      if (!signal.aborted) {
        setCapabilities(taken);
      }
    });
  }, [nextCapabilitiesSignal]);

  /** Releases the thread's key, but only if this window actually asked it something. */
  const leaveThread = useCallback((threadId: string | undefined) => {
    if (openThreads.current.leave(threadId)) {
      closeXbotThread(threadId);
    }
  }, []);

  const scrollListToLatest = useCallback((behavior: ScrollBehavior = "instant") => {
    const list = listRef.current;
    if (!list?.scrollBoxNativeElement) {
      return;
    }
    ignoreScrollRef.current = true;
    list.scrollTo({ top: "bottom", behavior });
    window.setTimeout(() => {
      ignoreScrollRef.current = false;
    }, 80);
  }, []);

  const onListScroll = useCallback(() => {
    if (ignoreScrollRef.current || !followLatestRef.current) {
      return;
    }
    const box = listRef.current?.scrollBoxNativeElement;
    if (box && !isNearListBottom(box)) {
      followLatestRef.current = false;
    }
  }, []);

  useLayoutEffect(() => {
    if (!followLatestRef.current) {
      return;
    }
    scrollListToLatest();
    if (busy) {
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        if (followLatestRef.current) {
          scrollListToLatest();
        }
        followLatestRef.current = false;
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [items, busy, scrollListToLatest]);

  /** A message still out belongs to the conversation being left; its answer must not land in the next one. */
  const dropTurn = useCallback(() => {
    abortChat();
    setBusy(false);
    followLatestRef.current = false;
  }, [abortChat]);

  // Items rebuild only when switching conversations, so a live conversation keeps its own item state (e.g. loaded table pages).
  useEffect(() => {
    dropTurn();
    if (!conversation) {
      return;
    }
    setItems(storedChatItems(conversation));
    // The draft stays, but the offer it had came for the conversation being left.
    setDraftId(uuidv4());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.localId]);

  /* Leaving a conversation - for a new one, one from the history, or with the chat gone - closes its thread. */
  const threadId = conversation?.threadId;
  useEffect(() => {
    if (!threadId) {
      return;
    }
    return () => leaveThread(threadId);
  }, [threadId, leaveThread]);

  // A message still in flight keeps its key until answered, so the thread only closes once nothing is waiting.
  useEffect(() => {
    if (!chatOpen && !busy) {
      leaveThread(threadId);
    }
  }, [chatOpen, busy, threadId, leaveThread]);

  /* The browser tab is going; every thread of this window is closed while the page can still send. */
  useEffect(() => {
    const onPageHide = () => openThreads.current.leaveAll().forEach(closeXbotThread);
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  // Defined above the effect that depends on it: a dep-array name is read during render, before a later definition would exist.
  const startNewConversation = useCallback(() => {
    dropTurn();
    setConversation(newXbotConversation(uuidv4(), uuidv4(), DateUtil.getDateISO()));
    setItems([]);
  }, [setConversation, dropTurn]);

  // The button starts clean: the page the chat was opened on is forgotten too, until another page offers itself.
  const startCleanConversation = useCallback(() => {
    setContext(null);
    startNewConversation();
  }, [startNewConversation]);

  useEffect(() => {
    if (!chatOpen) {
      return;
    }
    if (!conversation) {
      startNewConversation();
      return;
    }
    senderRef.current?.focus();
  }, [chatOpen, conversation, startNewConversation]);

  useEffect(() => {
    if (chatOpen) {
      refreshCapabilities();
    }
  }, [chatOpen, refreshCapabilities]);

  /* Reopening the panel on another page offers that page instead - including after a "forget". */
  useEffect(() => {
    setContext(pageContext);
  }, [pageContext]);

  /** `shown` overrides the thread line for a picked option; `sent` lets a refusal restore the typed draft with its picks. */
  const sendTurn = useCallback((outgoing: XbotOutgoing, shown?: string, sent?: SentDraft) => {
    if (!conversation || !hasSomethingToSend(outgoing)) {
      return;
    }
    // A new send replaces any in-flight turn immediately: its signal is aborted, so its eventual answer is dropped.
    const signal = nextChatSignal();

    const spoken = (shown || outgoing.text || "").trim();
    if (!outgoing.answerTo && !shown) {
      resetDraft();                                 // only the sender box is cleared; a click leaves a draft alone
    }
    followLatestRef.current = true;
    setBusy(true);
    const pendingKey = nextKey("ai");
    pendingKeyRef.current = pendingKey;
    const thread = threadOf(conversation);
    openThreads.current.asked(thread);

    setItems((prev) => [
      ...beforeNewTurn(prev),
      { key: nextKey("user"), role: "user", content: spoken, sent: outgoing, draft: sent },
      { key: pendingKey, role: "assistant", content: "", pending: true },
    ]);

    /** A line of the conversation, kept in the person's history. */
    const remember = (entry: XbotHistoryMessage) => {
      conversation.messages.push(entry);
      conversation.updated = DateUtil.getDateISO();
      setHistory((prev) => upsertPyxbotHistory(prev, conversation, signedInHistoryOwner()));
    };
    if (conversation.messages.length < 1) {
      conversation.topic = spoken;
    }
    remember(userEntry(spoken));

    // The page travels with every message while the bar is up; forgetting it stops that.
    const body = xbotChatBody(outgoing, thread, uuidv4(), context);
    // A refusal after the turn began is an answer in the thread; only one before it gives the draft back.
    let turnBegan = false;
    const onTurn = (turn: XbotTurn) => {
      turnBegan = true;
      if (!signal.aborted) {
        setItems((prev) => withTurn(prev, pendingKey, turn));
      }
    };
    void sendXbotChat(body, signal, onTurn).then((outcome) => {
      // Stop, a newer message or another conversation has put this turn away already
      if (signal.aborted) {
        return;
      }
      setBusy(false);
      if (outcome.kind === "refused") {
        // A picks refusal restores them into the editor, struck through — unless the person already typed something else.
        let restore: Draft | undefined;
        if (sent?.bindings.length && !turnBegan) {
          const back = restoredDraft(sent, outcome.error);
          const now = draftRef.current.text;
          if (!now.trim() || now === sent.message) {
            showDraft(back);
          } else {
            restore = back;
          }
          if (outcome.status === 501 || outcome.error.code === "unsupported") {
            refreshCapabilities();
          }
        }
        const refusal: XbotChatItem = { key: nextKey("error"), role: "error", content: outcome.error.message, restore };
        // an answer to a question that never reached the service leaves the question open to be answered again
        const unanswered = outgoing.answerTo && !turnBegan;
        setItems((prev) => settleTurn(unanswered ? withoutAnswerLine(prev, pendingKey) : prev, pendingKey, refusal));
      } else if (outcome.kind === "message") {
        const message = outcome.message;
        remember(assistantEntry(message));
        setConversation({ ...conversation });
        setItems((prev) => settleTurn(prev, pendingKey, { key: pendingKey, role: "assistant", content: answerText(message), message }));
        setHeard((prev) => (prev === ANSWER_HEARD ? `${ANSWER_HEARD}\u00a0` : ANSWER_HEARD));
      }
    });
  }, [conversation, context, setConversation, setHistory, resetDraft, showDraft, refreshCapabilities, nextChatSignal]);

  // The thread id for the first message comes from the server's starters response, and is handed back when the window leaves.
  const onStarters = useCallback((given: XbotStarters, page: XbotPageContext | null | undefined) => {
    setStarters({ page, starters: given });
    const thread = threadAfterStarters(conversation, given);
    if (!conversation || !thread) {
      return;
    }
    openThreads.current.asked(thread);
    if (conversation.threadId !== thread) {
      conversation.threadId = thread;
      setConversation({ ...conversation });
    }
  }, [conversation, setConversation]);

  // The page's object as NG answered it, only for the page the conversation still carries.
  const pageAnswer = starters?.page === context ? starters?.starters ?? null : null;

  /** A whole sentence from elsewhere - an example, a suggested question - goes the way a typed one goes. */
  const sendSentence = useCallback((sentence: string) => {
    const finished = finalizeDraft({ text: sentence || "", bindings: [] });
    if ("problem" in finished) {
      setComposerNote(finished.problem);
      return;
    }
    sendTurn({ text: finished.message });
  }, [sendTurn]);

  // A conversation from the history may have no thread yet; the first offer names the one it will use.
  const threadForSuggest = () => (conversationRef.current ? threadOf(conversationRef.current) : null);

  const suggestions = useSuggestions({
    draftId,
    capabilities,
    getThreadId: threadForSuggest,
    getInput: () => senderRef.current?.inputElement ?? null,
    getDraft: () => draftRef.current,
    onPicked: (next, caret) => {
      caretAfterRef.current = caret;
      showDraft(next);
    },
    onUndo: () => stepHistory(true),
    onRedo: () => stepHistory(false),
    onThreadAsked: (thread) => openThreads.current.asked(thread),
    onUnsupported: refreshCapabilities,
  });

  /** Restores the caret to the edit position instead of the end of the restored text. */
  function stepHistory(back: boolean) {
    const moved = back ? undo(historyRef.current) : redo(historyRef.current);
    if (moved === historyRef.current) {
      return;
    }
    const caret = caretAfterJump(draftRef.current.text, moved.present.text);
    if (caret !== null) {
      caretAfterRef.current = caret;
    }
    historyRef.current = moved;
    draftRef.current = moved.present;
    setDraft(moved.present);
    setComposerNote(null);
  }

  // After a pick the caret goes right after the inserted name; after undo/redo, back to the edit position.
  useLayoutEffect(() => {
    const caret = caretAfterRef.current;
    if (caret === null) {
      return;
    }
    caretAfterRef.current = null;
    placeCaret(caret);
  }, [draft]);

  function placeCaret(at: number) {
    const box = senderRef.current?.inputElement as HTMLTextAreaElement | undefined;
    if (typeof box?.setSelectionRange === "function") {
      box.focus();
      box.setSelectionRange(at, at);
    }
  }

  /** The editor's text changed: the picks follow their words, and the offer looks at the word at the cursor. */
  const onDraftChange = (value: string, event?: { target?: unknown }) => {
    const before = draftRef.current;
    const caret = caretOf(event?.target ?? senderRef.current?.inputElement);
    const next = applyEdit(before, value, caret?.end);
    if (next === before) {
      return;
    }
    showDraft(next, isSmallEdit(before, next));
    if (!value && before.text) {
      setDraftId(uuidv4());
    }
    suggestions.onChange(next);
  };

  /** The Objects search shut; on Escape the cursor goes back to where it was in the editor. */
  const closeSearch = (back: boolean) => {
    const open = suggestions.search;
    suggestions.closeSearch();
    if (back && open) {
      placeCaret(open.word ? open.word.end : open.start);
    }
  };

  /** An object found by name: it replaces the word the search opened with, or goes in at the cursor. */
  const onSearchPick = (item: XbotSuggestion) => {
    const open = suggestions.search;
    if (!open) {
      return;
    }
    const current = draftRef.current;
    const replaced = open.word ? acceptSuggestion(current, { start: open.word.start, end: open.word.end }, open.word.fragment, item) : null;
    const inserted = replaced ? { draft: replaced, caret: open.start + item.insert_text.length } : insertSuggestionAt(current, open.start, item);
    suggestions.closeSearch();
    if (!inserted) {
      setComposerNote(SEARCH_PICK_FAILED);
      return;
    }
    caretAfterRef.current = inserted.caret;
    showDraft(inserted.draft);
  };

  const onRemoveBinding = (index: number) => showDraft(removeBinding(draftRef.current, index));

  /** A refused message the person asked back into the editor; the refusal's button goes, it has done its work. */
  const onRestore = useCallback((key: string, back: Draft) => {
    showDraft(back);
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, restore: undefined } : item)));
  }, [showDraft]);

  /** Finalizes the draft once here — trims, validates, and blocks picks the service doesn't support. */
  const onSendMessage: SenderProps["onSubmit"] = (message) => {
    const typedText = message ?? "";
    const current = typedText === draftRef.current.text ? draftRef.current : applyEdit(draftRef.current, typedText);
    const finished = finalizeDraft(current);
    if ("problem" in finished) {
      setComposerNote(finished.problem);
      return;
    }
    if (finished.bindings.length && !capabilities?.bindings) {
      setComposerNote(PICKS_NEED_UPDATE);
      return;
    }
    suggestions.close();
    sendTurn({ text: finished.message, bindings: finished.bindings }, undefined, sentDraftOf(current) ?? undefined);
  };

  const onQuestionAnswer = useCallback((answer: XbotAnswerToQuestion, shownText: string) => {
    if (busy) {
      return;
    }
    sendTurn({ text: "", answerTo: answer }, shownText);
  }, [busy, sendTurn]);

  // Try again sends the line exactly as it went: its picked objects, the question it answered, and the draft it
  // came from - without that last one a refused retry would have no picks left to offer back.
  const onRetry = useCallback((asked: AskedBefore) => {
    if (busy) {
      return;
    }
    sendTurn(asked.outgoing, asked.shown, asked.draft);
  }, [busy, sendTurn]);

  const onSuggestPick = useCallback((sentence: string) => {
    if (busy) {
      return;
    }
    sendSentence(sentence);
  }, [busy, sendSentence]);

  const openLink = useCallback((path: string) => {
    navigate(path);
    // a large window steps aside, so the page it opened is seen
    onPageOpened?.();
  }, [navigate, onPageOpened]);

  /** Stops waiting. The service finishes or drops the turn on its own; its answer is not shown. */
  const cancelRequest = () => {
    abortChat();
    // read now: the update below runs later, when a new turn may have its own key
    const pendingKey = pendingKeyRef.current;
    setItems((prev) => afterStop(prev, pendingKey));
    setBusy(false);
  };

  const newestAnswer = useMemo(() => lastAnswerKey(items), [items]);

  // Right or wrong under an answer is offered only where NG does not run as production.
  const ratings = !GLOB.isProdEnv();

  const bubbleItems = useMemo<BubbleItemType[]>(() => items.map((item): BubbleItemType => {
    if (item.role === "user") {
      return { key: item.key, role: "user", content: item.content };
    }
    if (item.pending) {
      const turn = item.turn;
      return {
        key: item.key,
        role: "ai",
        content: "",
        contentRender: () => (turn
          // the same component as the settled answer below, so the blocks are not drawn (and read) afresh when it ends
          ? <XbotFramedAnswer message={turn.message} progress={turn.progress} loadPage={loadXbotPage} onOpenLink={openLink} />
          : <XbotProgressBar progress={null} />),
      };
    }
    const message = item.message;
    if (item.role === "assistant" && message) {
      // Only the newest answer still asks: its question can be answered and its suggestions sent.
      const live = item.key === newestAnswer && !busy;
      const asked = askedBefore(items, item.key);
      return {
        key: item.key,
        role: "ai",
        content: item.content,
        contentRender: () => <XbotFramedAnswer
          message={message}
          question={asked?.shown}
          loadPage={loadXbotPage}
          onAnswer={live ? onQuestionAnswer : undefined}
          onOpenLink={openLink}
          onRetry={live && asked ? () => onRetry(asked) : undefined}
        />,
        footer: <div className="xm-chat-answer-chrome">
          <XbotAnswerTools message={message} ratings={ratings} />
          <SuggestRow suggestions={message.suggestions} onPick={live ? onSuggestPick : undefined} />
        </div>,
      };
    }
    const restore = item.role === "error" ? item.restore : undefined;
    return {
      key: item.key,
      role: item.role === "error" ? "error" : "ai",
      content: item.content,
      footer: restore ? <Button size="small" onClick={() => onRestore(item.key, restore)}>Restore the message</Button> : undefined,
    };
  }), [items, busy, newestAnswer, onQuestionAnswer, onSuggestPick, onRetry, openLink, ratings, onRestore]);

  /**
   * Example questions belong to an untouched conversation only - see AIChatStarters. Not before the conversation is
   * there: the examples are asked for with its thread.
   */
  const showStarters = !busy && Boolean(conversation) && items.length === 0;

  return <div className="xm-chat-conversation">
    <div className="xm-chat-toolbar">
      <div className="xm-chat-topic">
        {/* Before the first question the topic is "New chat", the same words as the button beside it. */}
        {conversation?.messages?.length
          ? <span className="xm-chat-topic-name" title={conversation.topic}>{conversation.topic}</span>
          : <span className="xm-chat-topic-name xm-chat-topic-empty">No question yet</span>}
        {conversation?.updated && <span className="xm-chat-topic-time">{DateUtil.getShortDateTime(conversation.updated)}</span>}
      </div>
      <div className="xm-chat-actions">
        {/* Framed and labelled, since bare icons were overlooked; a narrow window keeps only the icons (aiChat.less). */}
        <Tooltip title="Start a new conversation">
          <Button size="small" aria-label="Start a new conversation" icon={<LuMessageSquarePlus />} onClick={startCleanConversation}>
            <span className="xm-chat-action-label">New chat</span>
          </Button>
        </Tooltip>
        <Tooltip title="Show conversations history">
          <Button size="small" aria-label="Show conversations history" icon={<LuHistory />} onClick={() => setHistoryOpen(true)}>
            <span className="xm-chat-action-label">History</span>
          </Button>
        </Tooltip>
      </div>
    </div>

    <AskingAboutBar context={context} about={pageAnswer?.about} note={pageAnswer?.note} onForget={() => setContext(null)} />

    <div className="xm-chat-heard" aria-live="polite">{heard}</div>
    <div className={showStarters ? "xm-chat-body xm-chat-body-starters" : "xm-chat-body"}>
      <Bubble.List
        ref={listRef}
        className="xm-chat-bubbles"
        autoScroll
        items={bubbleItems}
        role={ROLES}
        onScroll={onListScroll}
      />
      {showStarters && <AIChatStarters
        onPick={sendSentence}
        pageContext={context}
        threadId={conversation?.threadId}
        onStarters={onStarters}
      />}
    </div>

    <div className="xm-chat-composer">
      {suggestions.search && <ObjectSearch
        key={suggestions.search.key}
        listId={`${suggestions.listId}-search`}
        prefill={suggestions.search.word?.fragment ?? ""}
        start={suggestions.search.start}
        selected={suggestions.search.selected}
        getThreadId={threadForSuggest}
        onThreadAsked={(thread) => openThreads.current.asked(thread)}
        onPick={onSearchPick}
        onClose={closeSearch}
      />}
      {suggestions.offer && !suggestions.search && <SuggestionList
        listId={suggestions.listId}
        offer={suggestions.offer}
        active={suggestions.active}
        searchAll={suggestions.searchAll}
        onPick={suggestions.accept}
        onSearchAll={suggestions.searchAllOfNg}
      />}
      <Sender
        ref={senderRef}
        className="xm-chat-sender"
        value={draft.text}
        onChange={onDraftChange}
        onKeyDown={suggestions.onKeyDown}
        onBlur={suggestions.close}
        onSubmit={onSendMessage}
        onCancel={cancelRequest}
        loading={busy}
        autoSize={{ minRows: 1, maxRows: 5 }}
        placeholder="Ask me about your infrastructure..."
        footer={<div className="xm-chat-composer-footer">
          <SelectedRow bindings={draft.bindings} onRemove={onRemoveBinding} onPickAgain={suggestions.pickAgain} />
          <div className="xm-chat-composer-line">
            <SuggestButton disabled={suggestions.button.disabled} reason={suggestions.button.reason} onPress={suggestions.manual} />
            {(composerNote || suggestions.quiet) && <span className="xm-chat-composer-note" role="status">
              {composerNote || suggestions.quiet}
            </span>}
          </div>
        </div>}
      />
    </div>
  </div>;
};

/** Ant Design X pins the latest turn with column-reverse, so "bottom" is scrollTop ≈ 0. */
function isNearListBottom(box: HTMLElement): boolean {
  return (getComputedStyle(box).flexDirection === "column-reverse"
    ? Math.abs(box.scrollTop)
    : box.scrollHeight - box.scrollTop - box.clientHeight) <= 48;
}

/** The conversation's thread, named now when it has none yet. */
function threadOf(conversation: XbotHistory): string {
  conversation.threadId ||= uuidv4();
  return conversation.threadId;
}
